import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  HelpCircle, Book, Rocket, Settings, Users, Shield, 
  FileText, AlertTriangle, Plane, Moon, Brain, Search,
  ChevronRight, CheckCircle2, Circle, Clock, Target,
  Database, Key, Building2, ClipboardCheck, BarChart3,
  Zap, Calendar, Activity, Cpu, Mail, Upload, Download,
  Play, Monitor, Lock, Globe, Wrench, Lightbulb
} from 'lucide-react';
import ArticleAudioPlayer from '@/components/help/ArticleAudioPlayer';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function HelpCenterPage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('setup');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSection, setExpandedSection] = useState(null);

  // Deep-link support — when HelpTooltip nav'd here it stashes the article
  // id in sessionStorage. On mount, pre-populate the search box with the
  // article id/title so the user lands directly on the relevant guide.
  useEffect(() => {
    try {
      const target = sessionStorage.getItem('aeroguard_help_target_article');
      if (target) {
        setSearchQuery(target.replace(/-/g, ' '));
        setActiveTab('setup');
        sessionStorage.removeItem('aeroguard_help_target_article');
      }
    } catch { /* sessionStorage may be disabled */ }
  }, []);

  // Setup guide steps with detailed content
  const setupSteps = [
    {
      id: 'step-0',
      title: 'Prerequisites & System Requirements',
      icon: Monitor,
      status: 'complete',
      content: `
### System Requirements
- **Browser**: Chrome 90+, Firefox 88+, Safari 14+, Edge 90+
- **Screen Resolution**: Minimum 1280x720 (1920x1080 recommended)
- **Internet**: Stable connection with minimum 5 Mbps

### Before You Begin
1. Ensure you have administrator access credentials
2. Gather your organization's information (name, ICAO code, regulatory authority)
3. Prepare a list of initial users to be added
4. Have your company's SMS policy document ready for reference

### Supported Regulatory Frameworks
- ICAO Annex 19 (International)
- FAA Part 5 / Part 117 (USA)
- EASA Part-ORO / ORO.FTL (Europe)
- GACA GACAR Part 5 (Saudi Arabia)
      `
    },
    {
      id: 'step-1',
      title: 'Initial Login & Organization Setup',
      icon: Key,
      status: 'complete',
      content: `
### First Login
1. Navigate to the login page
2. Enter your administrator email and password
3. On first login, you may be prompted to change your password

### Organization Configuration
1. Go to **Administration → Organization Management**
2. Click **Edit Organization** or **Add Organization** for multi-org setups
3. Fill in required fields:
   - Organization Name
   - ICAO Code (4-letter identifier)
   - Regulatory Authority (GACA, FAA, EASA, etc.)
   - Contact Information
4. Upload your organization logo (recommended: PNG, 200x200px)
5. Save changes

### Multi-Organization Setup (Enterprise)
If managing multiple organizations:
1. Create parent organization first
2. Add subsidiary organizations with parent reference
3. Configure data sharing policies between organizations
      `
    },
    {
      id: 'step-2',
      title: 'User Management & Roles',
      icon: Users,
      status: 'complete',
      content: `
### Default System Roles
| Role | Access Level |
|------|--------------|
| **Admin** | Full system access, user management |
| **Safety Manager** | All safety modules, reports, analysis |
| **Quality Manager** | Audits, compliance, CAPAs |
| **Safety Officer** | Report incidents, view dashboards |
| **Pilot** | Submit reports, view personal data |
| **Inspector** | Audit capabilities, findings |
| **Executive** | High-level dashboards, KPIs |

### Adding Users
1. Go to **Administration → Enterprise Users**
2. Click **Add User**
3. Fill in:
   - Name (English & Arabic if applicable)
   - Email address
   - Initial password
   - Role assignment
   - Organization (for multi-org)
4. Click **Create User**

### Security Groups (Enterprise)
1. Create Security Groups to organize users by department
2. Assign Permission Sets to groups for fine-grained access control
3. Users inherit permissions from their groups

### LDAP/AD Integration
For enterprise environments:
1. Go to **Administration → AD / LDAP Sync**
2. Configure connection settings
3. Map LDAP groups to system roles
4. Enable auto-sync for automatic user provisioning
      `
    },
    {
      id: 'step-3',
      title: 'Core SMS Configuration',
      icon: Shield,
      status: 'complete',
      content: `
### Risk Matrix Setup
1. Go to **Risk Management → Settings**
2. Configure your 5x5 Risk Matrix:
   - Define severity levels (1-5)
   - Define likelihood levels (1-5)
   - Set risk tolerance thresholds (Green/Yellow/Red zones)
3. Save the matrix

### Incident Categories
Configure categories relevant to your operations:
- Flight Operations
- Ground Operations
- Maintenance
- Cabin Safety
- Security
- Fatigue
- Bird/Wildlife Strikes
- Weather Events

### Notification Settings
1. Configure email notifications for:
   - New incident reports
   - Risk threshold breaches
   - Overdue CAPAs
   - Audit findings
2. Set up escalation rules based on severity

### Safety Performance Indicators (SPIs)
1. Go to **Safety Assurance → Safety Performance**
2. Define your key SPIs:
   - Incident rate per 1000 flights
   - On-time CAPA closure rate
   - Audit finding closure rate
   - Voluntary reporting rate
3. Set targets and alert thresholds
      `
    },
    {
      id: 'step-4',
      title: 'Incident Reporting Module',
      icon: FileText,
      status: 'complete',
      content: `
### Configuring Report Forms
1. Navigate to **Safety Reporting → Settings**
2. Customize incident report fields:
   - Required vs optional fields
   - Dropdown options for categories
   - File attachment settings
3. Enable anonymous reporting option

### Submitting an Incident Report
1. Click **+ New Incident** or use the quick-report button
2. Fill in the form:
   - Date/Time of occurrence
   - Location (airport, airspace, ground)
   - Description of event
   - Contributing factors
   - Immediate actions taken
3. Attach relevant files (photos, documents)
4. Submit for review

### Incident Investigation Workflow
1. **Submitted** → Initial review by Safety Manager
2. **Under Investigation** → Root cause analysis
3. **Pending CAPA** → Corrective actions required
4. **Closed** → All actions completed

### Tips for Effective Reporting
- Be factual and objective
- Include time, location, and people involved
- Describe what happened, not who is to blame
- Attach supporting evidence
- Follow up on investigation outcomes
      `
    },
    {
      id: 'step-5',
      title: 'Risk Management Setup',
      icon: Target,
      status: 'complete',
      content: `
### Creating a Risk Register Entry
1. Go to **Risk Management → Risk Register**
2. Click **Add Risk**
3. Complete the risk assessment:
   - Risk title and description
   - Category and subcategory
   - Initial severity and likelihood scores
   - Current controls in place
   - Residual risk score
4. Assign risk owner
5. Save and monitor

### Bow-Tie Analysis
For major risks, create a Bow-Tie diagram:
1. Define the hazard (center)
2. Add threats (left side)
3. Add consequences (right side)
4. Map preventive barriers (left)
5. Map mitigating barriers (right)

### Risk Review Process
1. Schedule regular risk reviews (monthly recommended)
2. Update risk scores based on new data
3. Document changes and rationale
4. Archive closed risks

### Integration with Incidents
- High-severity incidents automatically create risk entries
- Link risks to related incidents for traceability
- Track risk trends over time
      `
    },
    {
      id: 'step-6',
      title: 'FDM/FOQA Integration',
      icon: Activity,
      status: 'complete',
      content: `
### About Flight Data Monitoring
FDM (Flight Data Monitoring) / FOQA (Flight Operational Quality Assurance) enables proactive safety management by analyzing flight data for exceedances.

### Data Integration Options
1. **File Upload**: CSV, XML, QAR formats
2. **API Integration**: Connect to data providers (FLYHT, Safran, etc.)
3. **Automated Sync**: Schedule regular data imports

### Setting Up FDM
1. Go to **Safety Assurance → FDM / FOQA**
2. Click the **Integration** tab
3. Configure your data source:
   - Upload files manually, OR
   - Set up provider webhook
4. Define exceedance parameters

### Analysis Rules
1. Navigate to the **Analysis** tab
2. Create rules for automatic event detection:
   - Event type (hard landing, unstable approach, etc.)
   - Threshold values
   - Auto-create incident option
3. Enable batch processing for historical data

### Reviewing Exceedances
1. View the **Exceedances** list
2. Filter by date, aircraft, event type
3. Review individual events with full context
4. Link to incidents if safety-relevant
      `
    },
    {
      id: 'step-7',
      title: 'FRMS (Fatigue Management)',
      icon: Moon,
      status: 'complete',
      content: `
### Fatigue Risk Management System
FRMS helps manage crew fatigue in compliance with:
- FAA Part 117
- ICAO Annex 6
- EASA ORO.FTL
- GACA regulations

### Initial Setup
1. Go to **Safety Assurance → FRMS (Fatigue)**
2. Click **Seed Demo Data** to populate sample data
3. Configure regulatory framework in settings

### Crew Member Registration
1. Navigate to the **Crew** tab
2. Add crew members with:
   - Employee ID and license
   - Role (Captain, FO, Cabin Crew)
   - Home base and timezone
   - Aircraft type ratings
3. Assign regulatory framework

### Duty Period Tracking
1. Use the **Duties** tab to log duty periods
2. Enter report/release times, sectors, flight time
3. System automatically calculates:
   - FDP compliance
   - Rest requirements
   - Cumulative limits

### Predictive Scheduling
1. Go to the **Scheduling** tab
2. Create or import a crew roster
3. Click **Analyze** to predict fatigue levels
4. Review the risk heatmap
5. Address conflicts before publishing

### Fatigue Reports
Crew can self-report fatigue using Samn-Perelli scale:
1. Report fatigue events with context
2. Track contributing factors
3. Enable anonymous reporting
      `
    },
    {
      id: 'step-8',
      title: 'AI Risk Prediction',
      icon: Brain,
      status: 'complete',
      content: `
### AI-Powered Safety Analytics
AeroGuard includes a hybrid AI system combining local pattern analysis with OpenAI GPT-5.2 for advanced safety predictions.

### Available Analysis Types
1. **Trend Analysis**: Identify patterns in safety data
2. **Root Cause Analysis**: AI-assisted RCA for incidents
3. **Recommendations**: Get actionable improvement suggestions
4. **Risk Predictions**: Predict potential future risks

### Running an Analysis
1. Go to **Safety Assurance → AI Risk Prediction**
2. Select the **Analyze** tab
3. Click on the analysis type you want
4. Wait for AI processing (typically 5-15 seconds)
5. Review results and recommendations

### Understanding Confidence Scores
- **Very High (90%+)**: Strong data support
- **High (70-90%)**: Good confidence
- **Medium (50-70%)**: Consider additional investigation
- **Low (<50%)**: Use as a starting point only

### Best Practices
- Run trend analysis monthly
- Use RCA for complex incidents
- Generate predictions quarterly
- Review AI recommendations in safety meetings
      `
    },
    {
      id: 'step-9',
      title: 'Audit Management',
      icon: ClipboardCheck,
      status: 'complete',
      content: `
### Types of Audits
- **Internal Audits**: Self-assessment against standards
- **External Audits**: Regulatory or third-party
- **Supplier Audits**: Vendor assessment
- **Enterprise Audits**: Cross-organization audits

### Creating an Audit
1. Go to **Safety Assurance → Audits**
2. Click **New Audit**
3. Define:
   - Audit type and scope
   - Audit date range
   - Auditor assignment
   - Checklist/standards reference
4. Create audit plan

### Conducting the Audit
1. Use the checklist to record findings
2. Attach evidence (photos, documents)
3. Classify findings by severity
4. Document non-conformances

### Finding Management
1. Each finding generates a CAPA item
2. Assign corrective actions
3. Set due dates
4. Track closure status

### Audit Programme
Use the **Audit Programme** feature to:
- Schedule recurring audits
- Maintain audit calendar
- Track auditor availability
- Generate compliance reports
      `
    },
    {
      id: 'step-10',
      title: 'Training & Currency',
      icon: Calendar,
      status: 'complete',
      content: `
### Training Management
Track crew and staff training requirements:
1. Go to **Safety Promotion → Training & Currency**
2. Define training types:
   - Initial training
   - Recurrent training
   - Type ratings
   - Emergency procedures
3. Set validity periods

### Currency Tracking
1. Add training records for personnel
2. System calculates expiry dates
3. Alerts generated before expiry
4. Dashboard shows upcoming expirations

### Training Reports
Generate reports for:
- Compliance status by department
- Upcoming training requirements
- Historical training records
- Cost analysis
      `
    },
    {
      id: 'step-11',
      title: 'Reports & Dashboards',
      icon: BarChart3,
      status: 'complete',
      content: `
### Executive Dashboard
The main dashboard provides:
- Safety KPIs at a glance
- Incident trends
- Risk distribution
- CAPA status
- Upcoming audits

### Standard Reports
Available reports include:
1. **Monthly Safety Report**: Comprehensive SMS summary
2. **Incident Analysis Report**: Detailed incident statistics
3. **Risk Register Report**: Current risk portfolio
4. **CAPA Status Report**: Corrective action progress
5. **Audit Summary Report**: Audit findings and trends

### Custom Reports
1. Use the Analytics module
2. Select data sources
3. Apply filters and date ranges
4. Choose visualization type
5. Export to PDF/Excel

### Scheduled Reports
Configure automatic report generation:
1. Select report type
2. Set frequency (daily/weekly/monthly)
3. Define recipients
4. Reports delivered via email
      `
    },
    {
      id: 'step-12',
      title: 'Go Live Checklist',
      icon: Rocket,
      status: 'pending',
      content: `
### Pre-Launch Verification
Complete this checklist before going live:

**Organization Setup**
- [ ] Organization details configured
- [ ] Logo uploaded
- [ ] Regulatory framework selected

**User Management**
- [ ] All users created
- [ ] Roles assigned correctly
- [ ] LDAP sync tested (if applicable)

**Core Configuration**
- [ ] Risk matrix configured
- [ ] Incident categories defined
- [ ] Notification settings verified
- [ ] Email integration tested

**Data Integration**
- [ ] FDM data source connected
- [ ] Historical data imported
- [ ] FRMS crew data loaded

**Training**
- [ ] Administrators trained
- [ ] Key users trained
- [ ] User guides distributed

**Testing**
- [ ] Test incident submitted
- [ ] Test risk created
- [ ] Test audit completed
- [ ] Reports generated successfully

### Post-Launch Support
- Monitor user adoption
- Address feedback promptly
- Schedule follow-up training
- Review SPIs after 30 days
      `
    }
  ];

  // Quick reference guides
  const quickGuides = [
    {
      title: 'How to Submit an Incident Report',
      steps: [
        'Click the "+ New Incident" button from any page',
        'Select the incident type and category',
        'Enter date, time, and location details',
        'Describe what happened in the narrative field',
        'Add any contributing factors or attachments',
        'Click Submit to send for review'
      ]
    },
    {
      title: 'How to Create a Risk Assessment',
      steps: [
        'Navigate to Risk Management → Risk Register',
        'Click "Add Risk" button',
        'Enter risk title and description',
        'Assess severity (1-5) and likelihood (1-5)',
        'Document existing controls',
        'Assign a risk owner and save'
      ]
    },
    {
      title: 'How to View Your Fatigue Score',
      steps: [
        'Go to Safety Assurance → FRMS (Fatigue)',
        'Navigate to the Crew tab',
        'Find your crew card',
        'Click the menu (⋮) and select "Assess Fatigue"',
        'View your current fatigue score and recommendations'
      ]
    },
    {
      title: 'How to Run AI Analysis',
      steps: [
        'Go to Safety Assurance → AI Risk Prediction',
        'Select the Analyze tab',
        'Choose analysis type (Trend, Root Cause, etc.)',
        'Click the corresponding button',
        'Wait for AI processing to complete',
        'Review insights and recommendations'
      ]
    }
  ];

  // FAQs
  const faqs = [
    // ------- AI-powered help features (v2–v9) ---------------------------
    {
      question: 'How does the AI Help Assistant work? (sparkle bubble)',
      answer: 'The sparkle bubble in the bottom-right of every page opens a GPT-5.2 assistant grounded ONLY in this help centre — it won\'t make up features. It supports Plain-English mode for lower-knowledge staff, remembers your last 3 exchanges for follow-ups like "and how do I close it?", speaks answers aloud, and lets you rate each reply with thumbs up/down.'
    },
    {
      question: 'How do I file a report by voice?',
      answer: 'Tap the red "File by Voice" button on the Incidents list, Wildlife Strikes page, or FRMS → Reports tab. Press the big red mic and describe what happened in English or Arabic. Live captions scroll on-screen so you can spot mis-hears. Tap stop → Transcribe & Extract. Whisper transcribes and GPT-5.2 extracts structured fields (title, ADREP category, severity, aircraft, species, hours awake, etc.). Review and Apply to prefill the form.'
    },
    {
      question: 'What are the "AI Suggestions" chips I see when I type?',
      answer: 'As you type a title/description on the Incidents, Wildlife, Risk, or Fatigue forms, GPT-5.2 reads your text and offers real-time chip suggestions for the trickier fields (Category, Severity, Likelihood, Species, Damage, Hours Awake, etc.). Click a chip to apply the value — it turns green with a checkmark when applied.'
    },
    {
      question: 'What does "Polish my description" do?',
      answer: 'Rewrites your rough draft into formal ICAO/GACAR aviation-safety register in one click. Fixes grammar and swaps casual words for precise terms ("aborted" → "rejected takeoff", "hit" → "ingested") but WILL NOT add facts you didn\'t mention. Preserves every acronym, aircraft reg, and quantitative datum verbatim.'
    },
    {
      question: 'Can I control the app by voice?',
      answer: 'Yes — tap the mic button in the bottom-right of any screen to opt in. Then say things like "close dialog", "clear the form", "submit", "next tab", "go to dashboard", "open wildlife", "open help center", or "stop listening". Uses browser Web Speech API — no LLM cost. Works in Chrome/Edge; not iOS Safari.'
    },
    {
      question: 'What are the "?" icons on form fields?',
      answer: 'Contextual help tooltips. Click the (?) next to a field label for a plain-English explanation, examples, and a "Learn more →" link. Many tooltips also include a "✨ Show me an example" button that fills the field with a valid sample so you see the correct format.'
    },
    {
      question: 'Can I re-take the guided tour?',
      answer: 'Yes — click the violet dashed "▶ Take the tour again" chip on top of the Dashboard. The tour is role-specific: safety managers see a different tour than auditors or investigators.'
    },
    {
      question: 'Can I listen to a help article instead of reading it?',
      answer: 'Yes — every help article and FAQ has a "🔊 Listen to this guide" button at the top of the answer. Uses the browser\'s built-in voice — works offline with no LLM cost. Play, Pause, Resume, and Stop controls all provided.'
    },
    // ------- Original FAQs ---------------------------------------------
    {
      question: 'Can I submit reports anonymously?',
      answer: 'Yes, AeroGuard supports anonymous reporting. When submitting an incident or fatigue report, check the "Submit anonymously" option. Your identity will not be recorded or visible to investigators.'
    },
    {
      question: 'How often should I run AI predictions?',
      answer: 'We recommend running trend analysis monthly and generating risk predictions quarterly. However, you can run analysis at any time after significant events or data updates.'
    },
    {
      question: 'What happens when a risk exceeds the tolerance threshold?',
      answer: 'When a risk score enters the red zone (high risk), the system automatically generates alerts to the Safety Manager and Risk Owner. The risk requires immediate attention and documented mitigation actions.'
    },
    {
      question: 'How do I know if my duty period is compliant?',
      answer: 'In the FRMS module, each duty period shows a compliance badge (Compliant/Violation). The system automatically checks against your regulatory framework limits for FDP, flight time, and rest requirements.'
    },
    {
      question: 'Can I integrate AeroGuard with our existing systems?',
      answer: 'Yes, AeroGuard provides API endpoints for integration with flight planning systems, crew management, maintenance systems, and more. Contact your administrator for API documentation.'
    },
    {
      question: 'How is my data protected?',
      answer: 'AeroGuard uses industry-standard encryption for data at rest and in transit. Access is controlled through role-based permissions. All data is backed up regularly and stored securely.'
    },
    {
      question: 'What regulatory frameworks are supported?',
      answer: 'AeroGuard supports ICAO Annex 19, FAA Part 5/Part 117, EASA Part-ORO/ORO.FTL, and GACA GACAR. The system can be configured for custom frameworks as well.'
    },
    {
      question: 'How do I get help if I have issues?',
      answer: 'For technical support, contact your system administrator. For training or feature requests, submit a request through the Help Center or contact your Safety Manager.'
    }
  ];

  // Module documentation
  const modules = [
    {
      name: 'Safety Reporting',
      icon: FileText,
      description: 'Submit and manage safety reports including incidents, hazards, and observations.',
      features: ['Incident reporting', 'Hazard identification', 'Voluntary reports', 'Near-miss tracking']
    },
    {
      name: 'Risk Management',
      icon: Target,
      description: 'Comprehensive risk assessment and management tools.',
      features: ['Risk register', 'Bow-Tie analysis', 'Risk matrix', 'Change management']
    },
    {
      name: 'Safety Assurance',
      icon: Shield,
      description: 'Continuous safety monitoring and improvement.',
      features: ['SPIs', 'FDM/FOQA', 'FRMS', 'AI Risk Prediction', 'Audits']
    },
    {
      name: 'Safety Promotion',
      icon: Lightbulb,
      description: 'Safety culture and training management.',
      features: ['Training records', 'Safety bulletins', 'Document library', 'Safety meetings']
    },
    {
      name: 'Compliance',
      icon: ClipboardCheck,
      description: 'Regulatory compliance tracking and evidence management.',
      features: ['Clause mapping', 'Evidence library', 'Gap analysis', 'Compliance dashboard']
    },
    {
      name: 'Administration',
      icon: Settings,
      description: 'System configuration and user management.',
      features: ['User management', 'Organization setup', 'LDAP/AD sync', 'Integrations']
    }
  ];

  // Filter content based on search
  const filteredSteps = setupSteps.filter(step => 
    step.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    step.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6 space-y-6" data-testid="help-center-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <HelpCircle className="h-7 w-7 text-blue-600" />
            {t('help.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('help.subtitle')}
          </p>
        </div>
        <Button 
          onClick={() => {
            localStorage.setItem('aeroguard_tour_active', 'true');
            localStorage.removeItem('aeroguard_tour_completed');
            window.location.href = '/';
          }}
          className="bg-purple-500 hover:bg-purple-600"
          data-testid="start-tour-btn"
        >
          <Play className="w-4 h-4 mr-2" />
          {t('help.tour')}
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder={t('common.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="help-search"
        />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Setup Progress</p>
                <p className="text-2xl font-bold">
                  {setupSteps.filter(s => s.status === 'complete').length}/{setupSteps.length}
                </p>
              </div>
              <Rocket className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Modules</p>
                <p className="text-2xl font-bold">{modules.length}</p>
              </div>
              <Database className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Quick Guides</p>
                <p className="text-2xl font-bold">{quickGuides.length}</p>
              </div>
              <Book className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">FAQs</p>
                <p className="text-2xl font-bold">{faqs.length}</p>
              </div>
              <HelpCircle className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="setup" data-testid="tab-setup">
            <Rocket className="h-4 w-4 mr-2" />
            Setup Guide
          </TabsTrigger>
          <TabsTrigger value="modules" data-testid="tab-modules">
            <Database className="h-4 w-4 mr-2" />
            Modules
          </TabsTrigger>
          <TabsTrigger value="features" data-testid="tab-features">
            <Book className="h-4 w-4 mr-2" />
            Feature Reference
          </TabsTrigger>
          <TabsTrigger value="guides" data-testid="tab-guides">
            <BarChart3 className="h-4 w-4 mr-2" />
            Quick Guides
          </TabsTrigger>
          <TabsTrigger value="faq" data-testid="tab-faq">
            <HelpCircle className="h-4 w-4 mr-2" />
            FAQ
          </TabsTrigger>
          <TabsTrigger value="support" data-testid="tab-support">
            <Mail className="h-4 w-4 mr-2" />
            Support
          </TabsTrigger>
        </TabsList>

        {/* Setup Guide Tab */}
        <TabsContent value="setup" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Rocket className="h-5 w-5 text-blue-500" />
                System Setup Guide
              </CardTitle>
              <CardDescription>
                Follow these steps to configure AeroGuard for your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Progress indicator */}
                <div className="flex items-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <CheckCircle2 className="h-5 w-5 text-blue-500" />
                  <span className="text-sm">
                    <strong>{setupSteps.filter(s => s.status === 'complete').length}</strong> of {setupSteps.length} steps complete
                  </span>
                  <div className="flex-1 h-2 bg-blue-100 rounded-full overflow-hidden ml-4">
                    <div 
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${(setupSteps.filter(s => s.status === 'complete').length / setupSteps.length) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Steps list */}
                <Accordion type="single" collapsible className="w-full">
                  {filteredSteps.map((step, index) => (
                    <AccordionItem key={step.id} value={step.id}>
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            step.status === 'complete' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                          }`}>
                            {step.status === 'complete' ? (
                              <CheckCircle2 className="h-5 w-5" />
                            ) : (
                              <span className="text-sm font-medium">{index}</span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium">{step.title}</p>
                            <p className="text-xs text-gray-500">Step {index}</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 pr-4 pb-4">
                          <div className="mb-2">
                            <ArticleAudioPlayer text={`${step.title}. ${step.content}`} />
                          </div>
                          <div className="prose prose-sm dark:prose-invert max-w-none">
                            <div className="whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                              {step.content.split('\n').map((line, i) => {
                                if (line.startsWith('###')) {
                                  return <h4 key={i} className="font-bold text-gray-900 dark:text-white mt-4 mb-2">{line.replace('###', '').trim()}</h4>;
                                }
                                if (line.startsWith('**') && line.endsWith('**')) {
                                  return <p key={i} className="font-semibold">{line.replace(/\*\*/g, '')}</p>;
                                }
                                if (line.startsWith('- [ ]')) {
                                  return (
                                    <div key={i} className="flex items-center gap-2 ml-4">
                                      <Circle className="h-4 w-4 text-gray-400" />
                                      <span>{line.replace('- [ ]', '').trim()}</span>
                                    </div>
                                  );
                                }
                                if (line.startsWith('- ')) {
                                  return (
                                    <div key={i} className="flex items-start gap-2 ml-4">
                                      <ChevronRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                                      <span>{line.replace('- ', '').trim()}</span>
                                    </div>
                                  );
                                }
                                if (line.match(/^\d+\./)) {
                                  return (
                                    <div key={i} className="flex items-start gap-2 ml-4">
                                      <span className="font-medium text-blue-600">{line.match(/^\d+/)[0]}.</span>
                                      <span>{line.replace(/^\d+\./, '').trim()}</span>
                                    </div>
                                  );
                                }
                                if (line.includes('|')) {
                                  return null; // Skip table rows for simplified display
                                }
                                return <p key={i}>{line}</p>;
                              })}
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modules.map((module, idx) => (
              <Card key={idx} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                      <module.icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{module.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{module.description}</p>
                      <div className="mt-3 space-y-1">
                        {module.features.map((feature, fidx) => (
                          <div key={fidx} className="flex items-center gap-2 text-xs">
                            <CheckCircle2 className="h-3 w-3 text-green-500" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Quick Guides Tab */}
        <TabsContent value="guides" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {quickGuides.map((guide, idx) => (
              <Card key={idx}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Play className="h-5 w-5 text-green-500" />
                    {guide.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ol className="space-y-2">
                    {guide.steps.map((step, sidx) => (
                      <li key={sidx} className="flex items-start gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium">
                          {sidx + 1}
                        </span>
                        <span className="text-sm">{step}</span>
                      </li>
                    ))}
                  </ol>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* FAQ Tab */}
        <TabsContent value="faq" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Frequently Asked Questions</CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {filteredFaqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`faq-${idx}`}>
                    <AccordionTrigger className="text-left">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="mb-1"><ArticleAudioPlayer text={`${faq.question}. ${faq.answer}`} /></div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 pb-4">
                        {faq.answer}
                      </p>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Support Tab */}
        <TabsContent value="support" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-500" />
                  Contact Support
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-medium mb-2">Technical Support</h4>
                  <p className="text-sm text-gray-500">For system issues and technical questions</p>
                  <p className="text-sm font-medium mt-2">support@aeroguard.aero</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-medium mb-2">Training Requests</h4>
                  <p className="text-sm text-gray-500">Request additional training sessions</p>
                  <p className="text-sm font-medium mt-2">training@aeroguard.aero</p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <h4 className="font-medium mb-2">Feature Requests</h4>
                  <p className="text-sm text-gray-500">Suggest new features or improvements</p>
                  <p className="text-sm font-medium mt-2">feedback@aeroguard.aero</p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5 text-green-500" />
                  Resources
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  User Manual (PDF)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  Administrator Guide (PDF)
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <FileText className="h-4 w-4 mr-2" />
                  API Documentation
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Play className="h-4 w-4 mr-2" />
                  Video Tutorials
                </Button>
                <Button variant="outline" className="w-full justify-start">
                  <Globe className="h-4 w-4 mr-2" />
                  Release Notes
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* System Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Cpu className="h-5 w-5 text-purple-500" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">Version</p>
                  <p className="font-medium">2.0.0</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">Environment</p>
                  <p className="font-medium">Production</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">AI Engine</p>
                  <p className="font-medium">GPT-5.2</p>
                </div>
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500">Last Updated</p>
                  <p className="font-medium">{new Date().toLocaleDateString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Reference Tab */}
        <TabsContent value="features" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Book className="h-5 w-5 text-purple-500" />
                Complete Feature Reference
              </CardTitle>
              <CardDescription>
                Comprehensive documentation of every feature in AeroGuard SMS
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-2">
                {/* Safety Reporting Features */}
                <AccordionItem value="safety-reporting" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-orange-500" />
                      <div>
                        <h4 className="font-medium">Safety Reporting Module</h4>
                        <p className="text-sm text-gray-500">Hazards, occurrences, wildlife strikes, bulletins</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Hazard/Occurrence Reports</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Create new safety reports (hazard, occurrence, near-miss)</li>
                          <li>• Anonymous reporting option</li>
                          <li>• AI-powered category classification</li>
                          <li>• Severity and likelihood assessment</li>
                          <li>• File attachments and photos</li>
                          <li>• Workflow tracking (Draft→Review→Investigation→Closed)</li>
                          <li>• Similar report detection</li>
                          <li>• ECCAIRS 2 XML export</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Wildlife Strike Reporting</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Dedicated wildlife strike form</li>
                          <li>• Species identification</li>
                          <li>• Strike location on aircraft</li>
                          <li>• Damage assessment</li>
                          <li>• Airport hotspot analytics</li>
                          <li>• Seasonal trend analysis</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Safety Bulletins</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Create and publish bulletins</li>
                          <li>• Target distribution by role/department</li>
                          <li>• Acknowledgment tracking</li>
                          <li>• Expiration dates</li>
                          <li>• Rich text editor</li>
                          <li>• Document attachments</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Risk Management Features */}
                <AccordionItem value="risk-management" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <div>
                        <h4 className="font-medium">Risk Management Module</h4>
                        <p className="text-sm text-gray-500">Risk register, bowtie, radar, lessons learned</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Risk Register</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Create and manage risks</li>
                          <li>• 5x5 risk matrix (severity × likelihood)</li>
                          <li>• Risk categories (Flight Ops, Ground, Maintenance, etc.)</li>
                          <li>• Control documentation</li>
                          <li>• Risk owner assignment</li>
                          <li>• Review scheduling</li>
                          <li>• Risk trend tracking</li>
                          <li>• Export to Excel/PDF</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Bowtie Analysis</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Visual bowtie diagram builder</li>
                          <li>• Hazard and top event definition</li>
                          <li>• Threat identification (left side)</li>
                          <li>• Consequence mapping (right side)</li>
                          <li>• Prevention barriers</li>
                          <li>• Mitigation barriers</li>
                          <li>• Barrier effectiveness tracking</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Risk Radar</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Real-time operational risk view</li>
                          <li>• Weather integration</li>
                          <li>• NOTAM awareness</li>
                          <li>• Crew fatigue status</li>
                          <li>• Maintenance status</li>
                          <li>• Alert thresholds</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Lessons Learned Library</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Capture insights from investigations</li>
                          <li>• AI-powered summarization</li>
                          <li>• Category tagging</li>
                          <li>• Search and filter</li>
                          <li>• Share across organization</li>
                          <li>• Link to training materials</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Safety Assurance Features */}
                <AccordionItem value="safety-assurance" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <Shield className="h-5 w-5 text-green-500" />
                      <div>
                        <h4 className="font-medium">Safety Assurance Module</h4>
                        <p className="text-sm text-gray-500">Audits, FDM/FOQA, digital twin, ERP</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Audit Management</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Plan internal/external audits</li>
                          <li>• Pre-built checklist library</li>
                          <li>• Custom checklist builder</li>
                          <li>• Auditor assignment</li>
                          <li>• Finding documentation</li>
                          <li>• Evidence attachments</li>
                          <li>• Corrective action (CAPA) tracking</li>
                          <li>• Audit report generation</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">FDM/FOQA Module</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Flight data analysis (8 tabs)</li>
                          <li>• Dashboard with key metrics</li>
                          <li>• Flight analysis and replay</li>
                          <li>• Exceedance detection</li>
                          <li>• Trend analysis</li>
                          <li>• AI-powered alerts</li>
                          <li>• Custom parameter monitoring</li>
                          <li>• Rule-based alerting</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Digital Twin</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Virtual operation model</li>
                          <li>• What-if scenario analysis</li>
                          <li>• Risk propagation modeling</li>
                          <li>• Resource planning</li>
                          <li>• Real-time synchronization</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Emergency Response (ERP)</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Response plan management (5 tabs)</li>
                          <li>• Contact lists</li>
                          <li>• Scenario checklists</li>
                          <li>• Resource inventory</li>
                          <li>• Exercise management</li>
                          <li>• Activation workflow</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Compliance Features */}
                <AccordionItem value="compliance" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <ClipboardCheck className="h-5 w-5 text-purple-500" />
                      <div>
                        <h4 className="font-medium">Compliance Center</h4>
                        <p className="text-sm text-gray-500">GAP analysis, document control, regulatory API</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">GAP Analysis</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Assess against ICAO, GACA, EASA, IOSA</li>
                          <li>• Compliance status tracking</li>
                          <li>• Evidence documentation</li>
                          <li>• Remediation planning</li>
                          <li>• Automated quarterly assessments</li>
                          <li>• Manual trigger option</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Regulatory Reporting</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• API configuration per authority</li>
                          <li>• Compliance dashboard</li>
                          <li>• Deadline notifications</li>
                          <li>• ECCAIRS 2 XML generation</li>
                          <li>• Email submission</li>
                          <li>• Direct API framework (Phase 3)</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Document Control</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Controlled document management</li>
                          <li>• Version control</li>
                          <li>• Approval workflows</li>
                          <li>• Distribution tracking</li>
                          <li>• Expiration alerts</li>
                          <li>• Full-text search</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* Assets Features */}
                <AccordionItem value="assets" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <Plane className="h-5 w-5 text-yellow-500" />
                      <div>
                        <h4 className="font-medium">Asset Management</h4>
                        <p className="text-sm text-gray-500">Aircraft, crew, maintenance, fleet analytics</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Crew Management</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• 7-step crew member creation wizard</li>
                          <li>• Personal info, passport, visa tracking</li>
                          <li>• Employment details</li>
                          <li>• License and medical tracking</li>
                          <li>• Emergency contacts</li>
                          <li>• Languages and type ratings</li>
                          <li>• Qualification expiry alerts</li>
                          <li>• FTL compliance monitoring</li>
                          <li>• Crew pairing analytics</li>
                          <li>• AI fatigue prediction (48h forecast)</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Maintenance Tracking</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Work order management</li>
                          <li>• MEL/CDL item tracking</li>
                          <li>• Defect management</li>
                          <li>• Parts inventory</li>
                          <li>• AD/SB compliance</li>
                          <li>• MSG-3/CAMP compliant</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">Aircraft Registry</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Fleet inventory</li>
                          <li>• Aircraft details and status</li>
                          <li>• Flight hours/cycles tracking</li>
                          <li>• Configuration management</li>
                          <li>• Incident linkage</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {/* AI Features */}
                <AccordionItem value="ai-features" className="border rounded-lg px-4">
                  <AccordionTrigger className="text-left">
                    <div className="flex items-center gap-3">
                      <Brain className="h-5 w-5 text-pink-500" />
                      <div>
                        <h4 className="font-medium">AI-Powered Features</h4>
                        <p className="text-sm text-gray-500">GPT-5.2 risk analysis, assistant, fatigue prediction</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pt-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">AI Risk Analysis</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Automated risk scoring</li>
                          <li>• Trend prediction</li>
                          <li>• Pattern detection</li>
                          <li>• Root cause suggestions</li>
                          <li>• Mitigation recommendations</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">AI Safety Assistant</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• Conversational AI interface</li>
                          <li>• Safety policy questions</li>
                          <li>• Regulatory guidance</li>
                          <li>• Report drafting assistance</li>
                          <li>• Incident summarization</li>
                        </ul>
                      </div>
                      <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <h5 className="font-medium mb-2">AI Fatigue Prediction</h5>
                        <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                          <li>• 48-hour fatigue risk forecast</li>
                          <li>• Timeline visualization</li>
                          <li>• Contributing factors analysis</li>
                          <li>• Scheduling recommendations</li>
                          <li>• Urgent alerts dashboard</li>
                          <li>• Individual crew analysis</li>
                        </ul>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
